import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import {
  createPairingSession,
  generatePairingCode,
  type TvPairingState,
  type TvPairingSession,
} from '@/mocks/tvApp';
import { useAudioPlayer } from '@/context/AudioContext';

// ── Remote Control Actions ──
export interface TvRemoteActions {
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  stop: () => void;
  nextStation: () => void;
  prevStation: () => void;
  volumeUp: () => void;
  volumeDown: () => void;
  mute: () => void;
  unmute: () => void;
  toggleMute: () => void;
}

interface TvPairingContextType {
  session: TvPairingSession;
  isDemoMode: boolean;
  remote: TvRemoteActions;
  generateNewCode: () => void;
  connectDevice: (deviceName?: string) => void;
  disconnectDevice: () => void;
  setError: () => void;
  renameDevice: (name: string) => void;
  setDeviceName: (name: string) => void;
}

const TvPairingContext = createContext<TvPairingContextType | null>(null);

const SESSION_STORAGE_KEY = 'rfw_tv_pairing_session';
const DEMO_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

function loadSession(): TvPairingSession {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (raw) {
      const parsed: TvPairingSession = JSON.parse(raw);
      // Validate expiry
      if (parsed.state === 'WAITING' && Date.now() > parsed.expiresAt) {
        return { ...parsed, state: 'EXPIRED' };
      }
      if (parsed.state === 'CONNECTED_MOCK') {
        return parsed; // connected sessions persist across refreshes (demo)
      }
      return parsed;
    }
  } catch { /* ignore */ }
  return createPairingSession();
}

function saveSession(session: TvPairingSession) {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch { /* ignore */ }
}

export function TvPairingProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<TvPairingSession>(loadSession);
  const { play, pause, toggle, stop, setVolume, stations, currentStation } = useAudioPlayer();

  // ── Expiry timer ──
  useEffect(() => {
    if (session.state !== 'WAITING') return;
    const remaining = session.expiresAt - Date.now();
    if (remaining <= 0) {
      setSession(prev => {
        const next = { ...prev, state: 'EXPIRED' as TvPairingState };
        saveSession(next);
        return next;
      });
      return;
    }
    const timer = setTimeout(() => {
      setSession(prev => {
        const next = { ...prev, state: 'EXPIRED' as TvPairingState };
        saveSession(next);
        return next;
      });
    }, remaining);
    return () => clearTimeout(timer);
  }, [session.state, session.expiresAt]);

  // ── Actions ──
  const generateNewCode = useCallback(() => {
    const fresh = createPairingSession();
    setSession(fresh);
    saveSession(fresh);
  }, []);

  const connectDevice = useCallback((deviceName?: string) => {
    setSession(prev => {
      const next: TvPairingSession = {
        ...prev,
        state: 'CONNECTED_MOCK',
        connectedAt: Date.now(),
        deviceName: deviceName || prev.deviceName,
      };
      saveSession(next);
      return next;
    });
  }, []);

  const disconnectDevice = useCallback(() => {
    setSession(prev => {
      const next: TvPairingSession = {
        ...prev,
        state: 'DISCONNECTED',
        connectedAt: null,
      };
      saveSession(next);
      return next;
    });
  }, []);

  const setError = useCallback(() => {
    setSession(prev => {
      const next: TvPairingSession = { ...prev, state: 'ERROR' };
      saveSession(next);
      return next;
    });
  }, []);

  const renameDevice = useCallback((name: string) => {
    setSession(prev => {
      const next: TvPairingSession = { ...prev, deviceName: name };
      saveSession(next);
      return next;
    });
  }, []);

  const setDeviceName = renameDevice;

  // ── Remote Control Actions ──
  const [muted, setMuted] = useState(false);
  const [vol, setVol] = useState(80);

  const remote: TvRemoteActions = {
    play: () => { if (currentStation) play(currentStation); },
    pause: () => pause(),
    togglePlay: () => toggle(),
    stop: () => { stop(); },
    nextStation: () => {
      const activeStations = stations.filter(s => s.isLive);
      if (activeStations.length === 0 || !currentStation) return;
      const idx = activeStations.findIndex(s => s.id === currentStation.id);
      const next = activeStations[(idx + 1) % activeStations.length];
      play(next);
    },
    prevStation: () => {
      const activeStations = stations.filter(s => s.isLive);
      if (activeStations.length === 0 || !currentStation) return;
      const idx = activeStations.findIndex(s => s.id === currentStation.id);
      const prev = activeStations[(idx - 1 + activeStations.length) % activeStations.length];
      play(prev);
    },
    volumeUp: () => {
      setVol(v => {
        const next = Math.min(100, v + 10);
        setVolume(next);
        return next;
      });
    },
    volumeDown: () => {
      setVol(v => {
        const next = Math.max(0, v - 10);
        setVolume(next);
        return next;
      });
    },
    mute: () => { setMuted(true); setVolume(0); },
    unmute: () => { setMuted(false); setVolume(vol); },
    toggleMute: () => {
      if (muted) {
        setMuted(false);
        setVolume(vol);
      } else {
        setMuted(true);
        setVolume(0);
      }
    },
  };

  const isDemoMode = session.state === 'CONNECTED_MOCK' || session.state === 'WAITING' || session.state === 'EXPIRED';

  return (
    <TvPairingContext.Provider
      value={{
        session,
        isDemoMode,
        remote,
        generateNewCode,
        connectDevice,
        disconnectDevice,
        setError,
        renameDevice,
        setDeviceName,
      }}
    >
      {children}
    </TvPairingContext.Provider>
  );
}

export function useTvPairing() {
  const ctx = useContext(TvPairingContext);
  if (!ctx) {
    throw new Error('useTvPairing must be used within TvPairingProvider');
  }
  return ctx;
}